import React from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';
import {Button, Space} from 'antd';
import {
  StyledUserReview,
  StyledUserReviewActions,
  StyledUserReviewUserDetails,
  StyledUserReviewDetails,
  StyledUserReviewStatusDetails,
  StyledUserReviewMessage,
  StyledUserReviewDetailsEditButton
} from './StyledUserReview';
import {CheckCircleIcon, CheckIcon, CloseCircleIcon, CloseIcon} from '../../icons';
import UserSelectDropdown from '../UserSelectDropdown';

const ReviewButtons = ({ approveButtonProps, rejectButtonProps, ...rest } = {}) => {
  const [t] = useTranslation('main');

  return (
    <StyledUserReviewActions {...rest}>
      <Button icon={<CloseIcon />} size='small' {...rejectButtonProps}>
        {t('reject')}
      </Button>
      <Button icon={<CheckIcon />} size='small' type='primary' {...approveButtonProps}>
        {t('approve')}
      </Button>
    </StyledUserReviewActions>
  );
};


const UserReview = ({
  canEdit = false,
  canReassign = false,
  date,
  message,
  onApprove,
  onEdit,
  onReject,
  onReassign,
  reviewer,
  status = 'default',
  ...rest
}) => {
  const [t] = useTranslation('main');

  const isRejected = status === 'rejected';
  const isPending = status === 'pending';
  const isApproved = status === 'approved';

  return (
    <StyledUserReview
      status={status}
      {...rest}
    >
      <StyledUserReviewDetails>
        <Space size='small' className='d-flex-child'>
          <StyledUserReviewUserDetails
            avatarProps={{ size: 16 }}
            email={false}
            user={reviewer}
          />
          {(canReassign && isPending) && (
            <UserSelectDropdown
              arrow={true}
              hideSelected={true}
              listPropName='email'
              onSelect={onReassign}
              placement='bottomRight'
              selectedValue={reviewer.employee_id}
            >
              <StyledUserReviewDetailsEditButton type='link'>{t('reassign')}</StyledUserReviewDetailsEditButton>
            </UserSelectDropdown>
          )}
        </Space>

        {(isApproved || isRejected) && (
          <StyledUserReviewStatusDetails status={status}>
            <Space size='small' align='center'>
              {isApproved ? <CheckCircleIcon /> : <CloseCircleIcon />}
              <span>{t(`expenseReviewStatuses.${status}`)}, {date}</span>
            </Space>
            {canEdit && (
              <StyledUserReviewDetailsEditButton onClick={onEdit} type='link'>
                {t('edit')}
              </StyledUserReviewDetailsEditButton>
            )}
          </StyledUserReviewStatusDetails>
        )}
      </StyledUserReviewDetails>

      {isRejected && (
        <StyledUserReviewMessage status={status}>
          {message}
        </StyledUserReviewMessage>
      )}

      {(isPending && canEdit) && (
        <ReviewButtons approveButtonProps={{onClick: onApprove}} rejectButtonProps={{onClick: onReject}} />
      )}
    </StyledUserReview>
  );
}

UserReview.propTypes = {
  onApprove: PropTypes.func,
  onEdit: PropTypes.func,
  onReject: PropTypes.func,
  onReassign: PropTypes.func,
  status: PropTypes.oneOf(['approved', 'pending', 'rejected', 'default']),
}

UserReview.ReviewButtons = ReviewButtons;

export default UserReview;

